import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, Loader2, Crown, Shield, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Navbar from '../components/layout/Navbar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PremiumSuccessPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [featureType, setFeatureType] = useState('');
  const sessionId = searchParams.get('session_id');
  const feature = searchParams.get('feature');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/premium/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setFeatureType(response.data.feature_type);
        } else {
          // Keep polling
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId]);

  const getFeatureInfo = () => {
    switch (featureType || feature) {
      case 'identity_verification':
        return {
          icon: Shield,
          title: t('premium.verification.successTitle') || 'Identity Verified!',
          description: t('premium.verification.successDesc') || 'Your verified badge is now visible on your profile.',
          color: 'text-blue-500',
        };
      case 'featured_listing':
        return {
          icon: Star,
          title: t('premium.featured.successTitle') || 'Wedding Featured!',
          description: t('premium.featured.successDesc') || 'Your wedding is now featured and will appear at the top of search results.',
          color: 'text-amber-500',
        };
      case 'premium_account':
        return {
          icon: Crown,
          title: t('premium.account.successTitle') || 'Welcome to Premium!',
          description: t('premium.account.successDesc') || 'You now have access to all premium features for 30 days.',
          color: 'text-purple-500',
        };
      default:
        return {
          icon: CheckCircle,
          title: t('premium.success') || 'Purchase Complete!',
          description: t('premium.successDesc') || 'Thank you for your purchase.',
          color: 'text-green-500',
        };
    }
  };

  const featureInfo = getFeatureInfo();
  const FeatureIcon = featureInfo.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
                <CardTitle>{t('premium.processing') || 'Processing your purchase...'}</CardTitle>
                <CardDescription>{t('premium.pleaseWait') || 'Please wait a moment'}</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <FeatureIcon className={`h-16 w-16 mx-auto mb-4 ${featureInfo.color}`} />
                <CardTitle className="text-green-600">{featureInfo.title}</CardTitle>
                <CardDescription>{featureInfo.description}</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">❌</span>
                </div>
                <CardTitle className="text-red-600">{t('premium.errorTitle') || 'Something went wrong'}</CardTitle>
                <CardDescription>{t('premium.errorDesc') || 'We could not process your purchase. Please try again or contact support.'}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {status === 'success' && (
              <>
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  {t('premium.goToDashboard') || 'Go to Dashboard'}
                </Button>
                <Link to="/premium" className="block text-sm text-muted-foreground hover:text-primary">
                  {t('premium.viewAllFeatures') || 'View all premium features'}
                </Link>
              </>
            )}
            {status === 'error' && (
              <>
                <Button onClick={() => navigate('/premium')} className="w-full">
                  {t('common.tryAgain') || 'Try Again'}
                </Button>
                <Link to="/" className="block text-sm text-muted-foreground hover:text-primary">
                  {t('common.backToHome') || 'Back to Home'}
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PremiumSuccessPage;
